// Help & Glossary — a browsable, searchable version of the app's built-in
// product knowledge (apps/server/src/appGuide.ts) that was previously only
// reachable by knowing to open the Copilot chat and phrase a question.
// Same overlay/backdrop/Escape convention as CommandPalette/ConfirmDialog.
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { GuideEntryDto } from "@arcline/shared";
import { api } from "../lib/api";
import { c, font } from "../lib/theme";

export function GuidePanel() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [all, setAll] = useState<GuideEntryDto[] | null>(null);
  const [results, setResults] = useState<GuideEntryDto[] | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const openPanel = (): void => {
    setOpen(true); setQ("");
    if (!all) api.guide().then((r) => { setAll(r); setResults(r); }).catch(() => { setAll([]); setResults([]); });
    else setResults(all);
  };

  useEffect(() => { if (open) inputRef.current?.focus(); }, [open]);

  // Debounced server-side search (weighted keyword/topic/body scoring) once
  // there's a query; falls back to the full list when the box is empty.
  useEffect(() => {
    if (!open) return;
    if (!q.trim()) { setResults(all); return; }
    const t = setTimeout(() => { api.guideSearch(q.trim()).then(setResults).catch(() => {}); }, 150);
    return () => clearTimeout(t);
  }, [q, open, all]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => { if (e.key === "Escape") { e.preventDefault(); setOpen(false); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const go = (path: string): void => { navigate(path); setOpen(false); };

  return (
    <>
      <span onClick={openPanel} title="Help & Glossary — search how ArcLine works" role="button" aria-label="Help and glossary" className="arc-hdr-btn"
        style={{ display: "flex", alignItems: "center", gap: 6, font: `600 12.5px ${font.sans}`, letterSpacing: "0.04em", color: c.muted2, background: c.soft.tileHi, border: "none", borderRadius: 8, padding: "5px 10px", cursor: "pointer" }}>
        <span>?</span><span className="arc-label">Glossary</span>
      </span>
      {open && (
        <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(3,5,11,0.72)", display: "grid", placeItems: "center", zIndex: 200, paddingTop: "10vh" }}>
          <div role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}
            style={{ width: 560, maxWidth: "90vw", maxHeight: "72vh", display: "flex", flexDirection: "column", background: c.soft.card, borderRadius: 18, boxShadow: "0 24px 64px rgba(0,0,0,0.55)", overflow: "hidden" }}>
            <div style={{ padding: "16px 18px 12px" }}>
              <div style={{ font: `700 17px ${font.display}`, color: c.textHi, marginBottom: 10 }}>Help & Glossary</div>
              <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search how ArcLine works — “LOI”, “matching”, “SPV”…"
                style={{ width: "100%", boxSizing: "border-box", background: c.soft.cardHi, border: "none", borderRadius: 10, padding: "10px 12px", color: c.textHi, fontSize: 15, outline: "none" }} />
            </div>
            <div style={{ flex: 1, overflowY: "auto", padding: "0 8px 10px" }}>
              {results == null && <div style={{ padding: "10px 14px", fontSize: 14, color: c.muted }}>Loading…</div>}
              {results != null && results.length === 0 && <div style={{ padding: "10px 14px", fontSize: 14, color: c.muted, fontStyle: "italic" }}>No topics match “{q.trim()}”.</div>}
              {results?.map((r) => (
                <div key={r.id} onClick={() => go(r.path)}
                  style={{ padding: "11px 14px", borderRadius: 8, cursor: "pointer", marginBottom: 2 }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = c.soft.cardHi)}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                  <div style={{ font: `600 14.5px ${font.sans}`, color: c.textHi, marginBottom: 3 }}>{r.topic}</div>
                  <div style={{ font: `400 14px ${font.sans}`, color: c.muted2, lineHeight: 1.5 }}>{r.body}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
